import { Play, Pause, Download, Loader2, Gift, RotateCcw, AlertCircle, MessageSquare } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { SeriesEpisode } from '../../types/series'
import { useRetryFailedSeriesEpisode } from '../../api/series'
import {
  SERIES_PLAY_EPISODE_EVENT,
  SERIES_TOGGLE_EPISODE_EVENT,
  SERIES_EPISODE_PLAY_STATE_EVENT,
  type SeriesEpisodePlayStateDetail,
} from '../../lib/series-feedback-events'
import { GenerationEta } from './generation-eta'

interface SeriesEpisodeRowProps {
  episode: SeriesEpisode
  seriesId: string
  episodeDurationMinutes: number
  isFreeEpisode?: boolean
  hasFeedback?: boolean
  onGiveFeedback?: (episode: SeriesEpisode) => void
}

export function SeriesEpisodeRow({
  episode,
  seriesId,
  episodeDurationMinutes,
  isFreeEpisode,
  hasFeedback = false,
  onGiveFeedback,
}: SeriesEpisodeRowProps) {
  const retryMutation = useRetryFailedSeriesEpisode()
  const [isCurrentlyPlaying, setIsCurrentlyPlaying] = useState(false)

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SeriesEpisodePlayStateDetail>).detail
      if (detail.episodeId === episode.id) {
        setIsCurrentlyPlaying(detail.isPlaying)
      } else if (detail.isPlaying) {
        // Another episode started playing
        setIsCurrentlyPlaying(false)
      }
    }
    window.addEventListener(SERIES_EPISODE_PLAY_STATE_EVENT, handler as EventListener)
    return () => window.removeEventListener(SERIES_EPISODE_PLAY_STATE_EVENT, handler as EventListener)
  }, [episode.id])

  const triggerPlay = () => {
    if (!episode.audioFileUrl) return
    if (isCurrentlyPlaying) {
      const event = new CustomEvent(SERIES_TOGGLE_EPISODE_EVENT, { detail: { episodeId: episode.id } })
      window.dispatchEvent(event)
      return
    }
    const event = new CustomEvent(SERIES_PLAY_EPISODE_EVENT, { detail: { episodeId: episode.id } })
    window.dispatchEvent(event)
  }

  const handleRetry = () => {
    retryMutation.mutate({ seriesId, podcastId: episode.id })
  }

  const formatDuration = (seconds: number | null | undefined, isGenerating: boolean): string => {
    if (isGenerating) return 'Generating...'
    if (!seconds) return ''
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const isRetryableStatus = episode.status === 'failed' || episode.status === 'draft'
  const retryTriggered = retryMutation.isPending || (retryMutation.isSuccess && isRetryableStatus)
  const isGenerating = episode.status === 'generating' || retryTriggered
  const isFailed = episode.status === 'failed' && !retryTriggered
  const isDraft = episode.status === 'draft' && !retryTriggered
  const isRetryable = (isFailed || isDraft) && !retryTriggered

  return (
    <div className={`group flex items-center gap-3 sm:gap-4 rounded-2xl sm:rounded-3xl border p-3 sm:p-5 transition-all ${isFailed ? 'border-danger-200 bg-danger-50/50' : isDraft ? 'border-warning-200 bg-warning-50/50' : 'border-black/5 bg-white hover:border-black/10 hover:shadow-sm'}`}>
      {/* Play button / Status indicator */}
      <button
        onClick={isRetryable ? handleRetry : triggerPlay}
        disabled={isGenerating || (!episode.audioFileUrl && !isRetryable)}
        className={`flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 cursor-pointer items-center justify-center rounded-2xl transition-colors ${
          isFailed
            ? 'bg-danger-100 text-danger-600 hover:bg-danger-200'
            : isDraft
              ? 'bg-warning-100 text-warning-700 hover:bg-warning-200'
              : 'bg-brand-50 text-brand-600 hover:bg-brand-100 disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-400'
        }`}
        title={isRetryable ? 'Retry episode generation' : isCurrentlyPlaying ? 'Pause episode' : 'Play episode'}
      >
        {isGenerating ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : isRetryable ? (
          <RotateCcw className="h-5 w-5" />
        ) : isCurrentlyPlaying ? (
          <Pause className="h-5 w-5" />
        ) : (
          <Play className="h-5 w-5 pl-0.5" />
        )}
      </button>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm sm:text-base font-bold text-foreground line-clamp-2 sm:truncate sm:line-clamp-none">
          {episode.title}
          {isFreeEpisode && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-600 align-middle">
              <Gift className="h-2.5 w-2.5" />
              Free
            </span>
          )}
        </p>
        <div className="mt-1 sm:mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground/50">
          <span>{formatDate(episode.createdAt)}</span>
          {!isGenerating && !isFailed && episode.durationSeconds && (
            <>
              <span aria-hidden>·</span>
              <span>{formatDuration(episode.durationSeconds, false)}</span>
            </>
          )}
          {isGenerating && (
            <>
              <span aria-hidden>·</span>
              <span className="text-brand-600">
                <GenerationEta
                  startedAt={episode.createdAt}
                  targetDurationMinutes={episodeDurationMinutes}
                />
              </span>
            </>
          )}
          {isFailed && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1 text-danger-600">
                <AlertCircle className="h-3 w-3" />
                Generation failed
              </span>
            </>
          )}
          {isDraft && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1 text-warning-700">
                <AlertCircle className="h-3 w-3" />
                Generation incomplete
              </span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      {isRetryable && (
        <button
          onClick={handleRetry}
          disabled={retryMutation.isPending}
          className={`shrink-0 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isFailed ? 'text-danger-600 hover:bg-danger-100' : 'text-warning-700 hover:bg-warning-100'}`}
        >
          Retry
        </button>
      )}
      {episode.audioFileUrl && !isRetryable && onGiveFeedback && (
        <button
          onClick={() => onGiveFeedback(episode)}
          className="hidden sm:inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] px-3 py-2 text-xs font-semibold text-foreground/50 transition-colors hover:bg-black/5 hover:text-foreground"
          title={hasFeedback ? 'Update feedback' : 'Leave feedback'}
        >
          <MessageSquare className="h-4 w-4" />
          <span>{hasFeedback ? 'Update feedback' : 'Feedback'}</span>
        </button>
      )}
      {episode.audioFileUrl && (
        <div className="flex items-center gap-1">
          <a
            href={episode.audioFileUrl}
            download
            className="flex cursor-pointer items-center justify-center rounded-[10px] p-2 text-foreground/40 transition-colors hover:bg-black/5 hover:text-foreground"
            title="Download"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  )
}
